import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SimplebarAngularModule } from 'simplebar-angular';
import MetisMenu from 'metismenujs';

import { PermissionService } from '../../core/services/permission.service';
import { MenuItem } from '../horizontaltopbar/menu.model';
import {
  NavigationMenuService,
  type NavMenuItem,
} from '../horizontaltopbar/navigation-menu.service';
import { PendingCountsService } from '../horizontaltopbar/pending-counts.service';

/** True when any item in the tree asks for a pending-count badge. */
function hasPendingCountKey(items: readonly MenuItem[]): boolean {
  return items.some(
    (item) => Boolean(item.pendingCountKey) || hasPendingCountKey(item.subItems ?? []),
  );
}

/** Narrows menu data to a non-empty string[] for safe use with PermissionService. */
function nonEmptyStringArray(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  const allStrings = value.every((el): el is string => typeof el === 'string');
  return allStrings ? value : null;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  imports: [CommonModule, RouterModule, TranslateModule, SimplebarAngularModule],
})
export class SidebarComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() isCondensed = false;

  @ViewChild('componentRef') scrollRef: unknown;
  @ViewChild('sideMenu') sideMenu?: ElementRef<HTMLUListElement>;

  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly navigationMenu = inject(NavigationMenuService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly pendingCounts = inject(PendingCountsService);

  menuItems: NavMenuItem[] = [];
  private metisMenu: MetisMenu | null = null;
  private menuReady = false;
  /** The menu as the API returned it, before the permission filter narrows it for display. */
  private navMenu: NavMenuItem[] = [];
  private permissionsReady = false;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.scheduleMenuActivation();
      }
    });

    // Menu Management edits land here without a reload. The very first emission arrives while
    // `initialize()` is still awaiting permissions, and filtering before those are known would
    // hide everything — so it is only stored, and `initialize()` renders it once both are in.
    this.navigationMenu.changed$.pipe(takeUntilDestroyed()).subscribe((menu) => {
      this.navMenu = menu;
      if (this.permissionsReady) {
        this.renderMenu();
      }
    });
  }

  ngOnInit(): void {
    void this.initialize();
  }

  ngAfterViewInit(): void {
    // MetisMenu is created in initialize() once menuItems are populated. Initializing here on
    // an empty <ul> attaches handlers to nothing and leaks the instance once items render.
    if (this.menuReady) {
      this.rebuildMetisMenu();
    }
    this.scheduleMenuActivation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-init only when the parent toggles condensed mode AFTER first render; otherwise we'd
    // double-bind on the very first change pass while initialize() is still running.
    if (!('isCondensed' in changes) || changes['isCondensed'].firstChange || !this.menuReady) {
      return;
    }
    this.rebuildMetisMenu();
  }

  /**
   * Badge text for an item's pending count (A-D9). Reads the counts signal, so a pushed count
   * repaints only the badge — the accordion is never rebuilt for it.
   */
  pendingBadge(item: MenuItem): string | null {
    return this.pendingCounts.badgeFor(item.pendingCountKey);
  }

  hasItems(item: MenuItem): boolean {
    return (item.subItems?.length ?? 0) > 0;
  }

  private async initialize(): Promise<void> {
    // Both are needed before the first render, and neither depends on the other: the API has
    // already pruned the menu, and the permission filter below only re-applies the same rules.
    const [, menu] = await Promise.all([
      this.permissionService.loadPermissions(),
      this.navigationMenu.load(),
    ]);
    this.permissionsReady = true;
    this.navMenu = menu;
    this.renderMenu();
  }

  /** Applies the permission filter to the current menu and rebinds the accordion around it. */
  private renderMenu(): void {
    // Menu Management has already pruned its menu by permission and role, and the keys it carries
    // are the menu's own vocabulary — re-checking them against the permissions endpoint can
    // only drop an item the API deliberately included (and with it a group left childless). The
    // built-in fallback menu has never been filtered, so that one still has to be.
    this.menuItems = this.navigationMenu.usingFallback()
      ? this.filterMenuByPermission(this.navMenu)
      : this.navMenu;
    this.menuReady = true;
    if (hasPendingCountKey(this.menuItems)) this.pendingCounts.start();
    this.cdr.detectChanges();
    this.rebuildMetisMenu();
    this.scheduleMenuActivation();
  }

  /** Dispose the previous MetisMenu and bind a fresh one to the rendered <ul>.
   *  Deferred to the next frame so any pending DOM updates from @for are committed first. */
  private rebuildMetisMenu(): void {
    this.metisMenu?.dispose();
    this.metisMenu = null;
    requestAnimationFrame(() => {
      if (this.sideMenu) {
        this.metisMenu = new MetisMenu(this.sideMenu.nativeElement);
      }
    });
  }

  private filterMenuByPermission(items: NavMenuItem[]): NavMenuItem[] {
    return items
      .filter((item) => {
        const modules = nonEmptyStringArray(item.modules);
        if (modules !== null && !this.permissionService.hasAnyModule(modules)) {
          return false;
        }
        if (item.permission) {
          const perms: string[] = Array.isArray(item.permission)
            ? item.permission
            : [item.permission];
          if (!perms.some((p) => this.permissionService.hasPermission(p))) {
            return false;
          }
        }
        return true;
      })
      .map((item) => {
        if (item.subItems) {
          const filteredSubItems = this.filterMenuByPermission(item.subItems);
          return { ...item, subItems: filteredSubItems };
        }
        return item;
      })
      .filter((item) => !item.subItems || item.subItems.length > 0);
  }

  private currentPath(): string {
    const noQuery = this.router.url.split('?')[0] ?? '';
    const path = noQuery.split('#')[0] ?? '';
    return path.length > 0 ? path : '/';
  }

  private scheduleMenuActivation(): void {
    if (!this.menuReady) {
      return;
    }
    setTimeout(() => {
      this.activateMenuDropdown();
      this.scrollActiveIntoView();
    });
  }

  private removeAllClass(className: string): void {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  private activateMenuDropdown(): void {
    this.removeAllClass('mm-active');
    this.removeAllClass('mm-show');

    const links = document.getElementsByClassName('side-nav-link-ref');
    const paths: string[] = Array.from(links).map((link) => (link as HTMLAnchorElement).pathname);
    const claimed: string[][] = Array.from(links).map((link) =>
      ((link as HTMLElement).dataset['activePaths'] ?? '').split(',').filter((p) => p.length > 0),
    );

    const target = this.currentPath();
    const matchedIndex = this.findMatchingMenuIndex(paths, target, claimed);
    const menuItemEl = matchedIndex === -1 ? null : (links[matchedIndex] ?? null);
    if (!menuItemEl) {
      return;
    }
    this.applyMmActiveChain(menuItemEl);
  }

  /** Resolution strategy, in order:
   *    1. Exact path match — light up that link.
   *    2. A path the menu item claimed via `activePaths` — for pages outside its own tree
   *       (e.g. /order-management/add-cart → Saved Carts). Ahead of the guesses, since it
   *       was declared.
   *    3. Longest segment-prefix — handles child routes nested under the menu link
   *       (e.g. /product-management/products/edit/:id → /product-management/products).
   *    4. Sibling under the same first segment — handles features whose menu link points
   *       at /list while edit/add/details live at sibling paths (e.g. /shows-management/edit/:id
   *       → /shows-management/list, /users/edit/:id → /users/list). When multiple menu links
   *       share the first segment, prefer the one ending in `/list`. */
  private findMatchingMenuIndex(paths: string[], target: string, claimed: string[][]): number {
    const directIndex = paths.indexOf(target);
    if (directIndex !== -1) return directIndex;
    const claimedIdx = this.findClaimedIndex(claimed, target);
    if (claimedIdx !== -1) return claimedIdx;
    const prefixIdx = this.findLongestPrefixIndex(paths, target);
    if (prefixIdx !== -1) return prefixIdx;
    return this.findSameRootIndex(paths, target);
  }

  /** First menu link whose declared paths cover the target, itself or as a parent segment. */
  private findClaimedIndex(claimed: string[][], target: string): number {
    return claimed.findIndex((declared) =>
      declared.some((p) => target === p || target.startsWith(p + '/')),
    );
  }

  private findLongestPrefixIndex(paths: string[], target: string): number {
    let bestIdx = -1;
    let bestLen = 0;
    for (let i = 0; i < paths.length; i++) {
      const p = paths[i];
      if (!p || p === '/') continue;
      const isSegmentPrefix = target === p || target.startsWith(p + '/');
      if (isSegmentPrefix && p.length > bestLen) {
        bestLen = p.length;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  private findSameRootIndex(paths: string[], target: string): number {
    const targetRoot = this.firstSegment(target);
    if (!targetRoot) return -1;
    const candidates: number[] = [];
    for (let i = 0; i < paths.length; i++) {
      const p = paths[i];
      if (p && this.firstSegment(p) === targetRoot) candidates.push(i);
    }
    if (candidates.length === 0) return -1;
    if (candidates.length === 1) return candidates[0] ?? -1;
    // Tie-breaker: prefer the menu link ending in `/list` (e.g. /users/list over /users/session-log).
    const listMatch = candidates.find((i) => paths[i]?.endsWith('/list') ?? false);
    return listMatch ?? -1;
  }

  private firstSegment(path: string): string {
    return path.split('/').find((s) => s.length > 0) ?? '';
  }

  private applyMmActiveChain(menuItemEl: Element): void {
    menuItemEl.classList.add('active');
    const parentEl = menuItemEl.parentElement;
    if (!parentEl) return;
    parentEl.classList.add('mm-active');

    const parent2El = parentEl.parentElement?.closest('ul') ?? null;
    if (!parent2El || parent2El.id === 'side-menu') return;
    parent2El.classList.add('mm-show');

    const parent3El = parent2El.parentElement;
    if (!parent3El || parent3El.id === 'side-menu') return;
    parent3El.classList.add('mm-active');
    parent3El.querySelector('.has-arrow')?.classList.add('mm-active');
    parent3El.querySelector('.has-dropdown')?.classList.add('mm-active');

    const parent4El = parent3El.parentElement;
    if (!parent4El || parent4El.id === 'side-menu') return;
    parent4El.classList.add('mm-show');

    const parent5El = parent4El.parentElement;
    if (!parent5El || parent5El.id === 'side-menu') return;
    parent5El.classList.add('mm-active');
    const isParent = parent5El.querySelector('.is-parent');
    if (isParent && parent5El.id !== 'side-menu') {
      isParent.classList.add('mm-active');
    }
  }

  private scrollActiveIntoView(): void {
    const active = document.getElementsByClassName('mm-active')[0] as HTMLElement | undefined;
    if (!active) return;
    const currentPosition = active.offsetTop;
    const ref = this.scrollRef as { SimpleBar?: { getScrollElement(): HTMLElement } } | undefined;
    const scrollEl = ref?.SimpleBar?.getScrollElement?.();
    if (currentPosition > 500 && scrollEl) {
      scrollEl.scrollTop = currentPosition + 300;
    }
  }
}
